import { Skeleton } from '@nest/uikit-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTerminalClient } from '../client-rpc-router';
import { TerminalScreen } from '../components/TerminalScreen';

export default function DeliveryListPage() {
	const client = useTerminalClient();
	const navigate = useNavigate();

	const { data: routes } = useQuery({
		queryKey: ['shipment-routes-to-load'],
		queryFn: () => client.shipmentRoutes.getShipmentRoutesToLoadQuery.execute({})
	});

	const { data: remainingStates } = useQuery({
		queryKey: ['shipment-routes-remaining-states', routes?.map(r => r.id).join(',')],
		queryFn: () => client.shipments.getShipmentRouteRemainingItemsToPickQuery.execute({ shipmentRouteIds: (routes ?? []).map(r => r.id) }),
		enabled: (routes ?? []).length > 0
	});

	const { data: loadingInfo } = useQuery({
		queryKey: ['shipment-routes-loading-info', routes?.map(r => r.id).join(',')],
		queryFn: () => client.shipments.getShipmentRoutesLoadingInfoQuery.execute({ shipmentRouteIds: (routes ?? []).map(r => r.id) }),
		enabled: (routes ?? []).length > 0
	});

	const routeIdToMissingCount = new Map<number, number>((remainingStates ?? []).map(s => [s.shipmentRouteId, s.missingItems.length]));
	const routeIdToLoadingInfo = useMemo(() => new Map((loadingInfo ?? []).map(info => [info.shipmentRouteId, info])), [loadingInfo]);

	const getMissingCountColor = useCallback((missingCount: number | undefined) => {
		if (missingCount === undefined) {
			return 'bg-muted';
		}
		return missingCount > 0 ? 'bg-red-600' : 'bg-green-600';
	}, []);

	return (
		<TerminalScreen showBackButton topBar={<div>Rozvozy</div>}>
			<div className="flex flex-col h-full overflow-auto p-2 gap-2">
				{routes ? (
					routes.map(route => {
						const missingCount = routeIdToMissingCount.get(route.id);
						const loadingInfo = routeIdToLoadingInfo.get(route.id);
						const date = route.startAt ? new Date(route.startAt) : undefined;
						const dateText = date
							? new Intl.DateTimeFormat('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date)
							: '';

						return (
							<button key={route.id} type="button" onClick={() => navigate(`/delivery/list/${route.id}`)} className="w-full text-left hover:bg-muted/50">
								<div className="border border-border rounded-md p-2 flex items-center gap-3">
									<div className={`h-4 w-4 rounded-sm ${getMissingCountColor(missingCount)}`} aria-hidden />
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2 justify-between">
											<div>
												<div className="truncate font-medium">{route.vehicle?.name ?? ''}</div>
												<div className="text-muted-foreground">{dateText}</div>
											</div>
											<div className="flex flex-col items-center gap-2">
												{loadingInfo && <span className="bg-muted text-muted-foreground px-2 py-1 rounded-md">Množství: {loadingInfo.totalCount} ks</span>}
											</div>
										</div>
										{loadingInfo?.processedBy && (
											<span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-md">Zpracovává: {loadingInfo.processedBy.name}</span>
										)}
									</div>
								</div>
							</button>
						);
					})
				) : (
					<div className="p-3 space-y-2">
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
					</div>
				)}

				{routes && routes.length === 0 ? <div className="p-6 text-center text-muted-foreground">Žádné trasy k naložení</div> : null}
			</div>
		</TerminalScreen>
	);
}
